/**
 * 충돌 해결 다이얼로그
 * 토스 스타일: 바텀 시트 형태의 깔끔한 선택 UI
 */

import { ConflictResolution, formatConflictDetails } from "@/lib/db/conflict";
import type { ConflictInfo } from "@/lib/db/types";

interface ConflictDialogProps {
  conflict: ConflictInfo;
  onResolve: (resolution: ConflictResolution) => void;
  onCancel: () => void;
}

export function ConflictDialog({ conflict, onResolve, onCancel }: ConflictDialogProps) {
  const details = formatConflictDetails(conflict);

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString("ko-KR", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      {/* 백드롭 */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCancel} />

      {/* 바텀 시트 */}
      <div className="relative w-full max-w-lg animate-slide-up rounded-t-3xl bg-white pb-safe">
        {/* 핸들 바 */}
        <div className="flex justify-center pt-3">
          <div className="h-1 w-10 rounded-full bg-slate-200" />
        </div>

        {/* 헤더 */}
        <div className="px-6 pb-4 pt-4">
          <div className="mb-2 flex items-center gap-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-100">
              <svg className="h-6 w-6 text-amber-600" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 9v4m0 4h.01M5.313 20h13.374c1.505 0 2.471-1.6 1.77-2.931L13.77 4.363c-.733-1.373-2.807-1.373-3.54 0L3.543 17.069C2.842 18.4 3.808 20 5.313 20z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div>
              <h2 className="text-[18px] font-bold text-slate-900">데이터 충돌</h2>
              <p className="text-[14px] text-slate-500">어떤 버전을 사용할까요?</p>
            </div>
          </div>
        </div>

        {/* 충돌 정보 */}
        <div className="mx-6 mb-4 rounded-2xl bg-slate-50 p-4">
          <p className="mb-2 text-[13px] font-medium text-slate-500">변경된 항목</p>
          <div className="space-y-1">
            {details.map((detail, index) => (
              <p key={index} className="text-[14px] text-slate-700">
                • {detail}
              </p>
            ))}
          </div>
        </div>

        {/* 시간 비교 */}
        <div className="mx-6 mb-6 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-blue-50 p-4">
            <div className="mb-1 flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-500">
                <svg
                  className="h-3 w-3 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                >
                  <path d="M5 12h14M12 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="text-[13px] font-semibold text-blue-700">내 기기</span>
            </div>
            <p className="text-[14px] text-blue-600">{formatDate(conflict.localUpdatedAt)}</p>
          </div>
          <div className="rounded-2xl bg-emerald-50 p-4">
            <div className="mb-1 flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-emerald-500">
                <svg
                  className="h-3 w-3 text-white"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                >
                  <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <span className="text-[13px] font-semibold text-emerald-700">서버</span>
            </div>
            <p className="text-[14px] text-emerald-600">{formatDate(conflict.serverUpdatedAt)}</p>
          </div>
        </div>

        {/* 선택 옵션 */}
        <div className="space-y-2 px-6">
          <button
            onClick={() => onResolve(ConflictResolution.KEEP_LOCAL)}
            className="flex w-full items-center gap-4 rounded-2xl bg-blue-500 p-4 text-left transition-all active:scale-[0.98]"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
              <svg
                className="h-5 w-5 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <rect x="5" y="2" width="14" height="20" rx="2" />
                <path d="M12 18h.01" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-[15px] font-semibold text-white">내 버전 사용</p>
              <p className="text-[13px] text-blue-100">서버 데이터를 덮어씁니다</p>
            </div>
          </button>

          <button
            onClick={() => onResolve(ConflictResolution.KEEP_SERVER)}
            className="flex w-full items-center gap-4 rounded-2xl bg-emerald-500 p-4 text-left transition-all active:scale-[0.98]"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/20">
              <svg
                className="h-5 w-5 text-white"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <ellipse cx="12" cy="5" rx="9" ry="3" />
                <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-[15px] font-semibold text-white">서버 버전 사용</p>
              <p className="text-[13px] text-emerald-100">내 변경사항을 버립니다</p>
            </div>
          </button>

          <button
            onClick={() => onResolve(ConflictResolution.MERGE)}
            className="flex w-full items-center gap-4 rounded-2xl bg-slate-100 p-4 text-left transition-all active:scale-[0.98]"
          >
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-slate-200">
              <svg
                className="h-5 w-5 text-slate-600"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" strokeLinecap="round" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="text-[15px] font-semibold text-slate-700">자동 병합</p>
              <p className="text-[13px] text-slate-500">최신 데이터를 기준으로 합칩니다</p>
            </div>
          </button>
        </div>

        {/* 나중에 버튼 */}
        <div className="px-6 pb-6 pt-4">
          <button
            onClick={onCancel}
            className="w-full rounded-2xl py-4 text-[15px] font-medium text-slate-400 active:bg-slate-50"
          >
            나중에 결정
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConflictDialog;
